'use client';

import { useState, useRef, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import { Button, CircularProgress } from '@mui/material';
import { useServiceTypeList, deleteServiceType, fetchAllServiceType } from '../../../stores/serviceTypeStore';
import { useCompanyList } from '../../../stores/companyStore';
import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import FormDialog from '../../../components/FormDialog';
import ServiceTypeAddEdit from './ServiceTypeAddEdit';

import { useLanguage } from '../../../utils/i18n';

function ServiceTypePageContent() {
  const { t } = useLanguage();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);
  const formRef = useRef();
  const notify = useNotifier();
  const { companyOptions } = useCompanyList();
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      if (!stored) return;
      const match = companyOptionsNormalized.find((o) => String(o.value) === String(stored));
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: String(stored), label: String(stored) }];

      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from storage failed', e);
    }
  }, [companyOptionsNormalized]);

  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.all', 'All') },
      { value: 'service_type_name', label: t('serviceType.name', 'Name') },
      { value: 'service_length_unit', label: t('serviceType.scheduleBy', 'Schedule By') },
      { value: 'interval_distance', label: t('serviceType.distanceKm', 'Distance (Km)') },
      { value: 'interval_time', label: t('serviceType.days', 'Days') },
      { value: 'service_cost', label: t('serviceType.serviceCost', 'Service Cost') }
    ],
    [t]
  );

  const companyIdsString = useMemo(() => {
    if (!selectedCompanies || selectedCompanies.length === 0) return '';

    const hasAll = selectedCompanies.some((s) => String(s.value) === 'all');
    if (hasAll) return '';

    return selectedCompanies.map((s) => String(s.value)).join(',');
  }, [selectedCompanies]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = String(searchRaw ?? '').trim();
      setSearchValue((prev) => (prev === next ? prev : next));
      setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    }, 700);

    return () => clearTimeout(timer);
  }, [searchRaw]);

  const {
    data: rows,
    totalRecord: totalRecords,
    isLoading
  } = useServiceTypeList(paginationModel.page, paginationModel.pageSize, searchField, searchValue, companyIdsString);

  const handleFetchAllForExport = async () => {
    try {
      return await fetchAllServiceType({
        searchKey: searchField,
        searchValue,
        selectedCompanyIds: companyIdsString,
        total: totalRecords
      });
    } catch (err) {
      console.error('service type export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };
  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);

    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('error');
    }
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id) => {
    setConfirmTargetId(id);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteServiceType(confirmTargetId);
      notify({ message: t('serviceType.deletedSuccess', 'Deleted successfully'), severity: 'success' });
      setPaginationModel((p) => ({ ...p }));
    } catch (err) {
      console.error(err);
      notify({ message: t('serviceType.failedDelete', 'Failed to delete'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
    setLoadingForm(false);
  };
  const handleDialogSave = () => {
    const formValues = formRef.current?.getValues?.();
    if (formValues) {
      const { service_length_unit, tracehold, tracehold_time_warning, tracehold_distance, tracehold_distance_warning } = formValues;

      let isInvalid = false;
      let errorMsg = '';
      if (['distance', 'both'].includes(service_length_unit)) {
        if (Number(tracehold_distance) >= Number(tracehold_distance_warning)) {
          isInvalid = true;
          errorMsg = t('serviceType.dangerDistanceWarning', 'Threshold Danger by Distance must be smaller than Warning!');
        }
      }
      if (['time', 'both', 'engine_work_time'].includes(service_length_unit)) {
        if (Number(tracehold) >= Number(tracehold_time_warning)) {
          isInvalid = true;
          errorMsg = t('serviceType.dangerTimeWarning', 'Threshold Danger by Time/Engine must be smaller than Warning!');
        }
      }
      if (isInvalid) {
        notify({ message: errorMsg, severity: 'warning' });
        setLoadingForm(false);
        return;
      }
    }

    formRef.current?.submitForm?.();
  };

  const handleDialogSuccess = (msg) => {
    notify({ message: msg || t('serviceType.savedSuccess', 'Saved successfully'), severity: 'success' });
    handleDialogClose();
    setPaginationModel((p) => ({ ...p }));
    setLoadingForm(false);
  };
  const handleDialogError = (msg) => {
    notify({ message: msg || t('serviceType.operationFailed', 'Operation failed'), severity: 'error' });
    setLoadingForm(false);
  };
  const handleDialogSubmitStart = () => {
    setLoadingForm(true);
  };

  const handleDialogSubmitEnd = () => {
    setLoadingForm(false);
  };
  const handleSearch = ({ field, value }) => {
    setSearchField(field);
    setSearchRaw(value ?? '');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const getCompanyLabel = (row) => {
    if (!row) return '-';
    if (row?.join?.company_name) return row.join.company_name;
    const companyId = row?.company ?? row?.company_id ?? null;
    if (companyId && companyOptionsNormalized) {
      const found = companyOptionsNormalized.find((c) => String(c.value) === String(companyId));
      if (found) return found.label;
    }
    return '-';
  };
  const formatCurrency = (v) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(Number(v || 0));

  const columns = [
    { field: 'company', headerName: t('general.company', 'Company'), flex: 1.2, renderCell: (params) => getCompanyLabel(params.row) },
    { field: 'name', headerName: t('serviceType.name', 'Name'), flex: 1 },
    { field: 'service_length_unit', headerName: t('serviceType.scheduleBy', 'Schedule By'), flex: 1 },
    { field: 'interval_distance', headerName: t('serviceType.distanceKm', 'Distance (Km)'), flex: 1 },
    { field: 'interval_time', headerName: t('serviceType.days', 'Days'), flex: 1 },
    {
      field: 'service_cost',
      headerName: t('serviceType.serviceCost', 'Service Cost'),
      flex: 1,
      renderCell: (params) => formatCurrency(params.row.service_cost || 0)
    },
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      renderCell: (params) => <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={handleDelete} mode="inline" />
    }
  ];

  return (
    <div>
      <DataTable
        title={t('serviceType.title', 'Service Type')}
        rows={rows}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');

          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('serviceType.searchByName', 'Search by Name')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        hideExportButton={false}
        onFetchAllForExport={handleFetchAllForExport}
      />

      <FormDialog
        open={openDialog}
        onClose={handleDialogClose}
        title={editingId ? t('serviceType.editServiceType', 'Edit Service Type') : t('serviceType.addServiceType', 'Add Service Type')}
        maxWidth="md"
        fullHeight={false}
        actions={
          <>
            <Button onClick={handleDialogClose} sx={{ textTransform: 'none' }}>
              {t('buttonTitle.cancel', 'Cancel')}
            </Button>

            <Button
              variant="contained"
              disabled={loadingForm}
              onClick={handleDialogSave}
              sx={{ color: 'primary', textTransform: 'none' }}
              startIcon={loadingForm ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {loadingForm ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
            </Button>
          </>
        }
      >
        <ServiceTypeAddEdit
          ref={formRef}
          id={editingId}
          companyOptions={companyOptionsNormalized}
          onClose={handleDialogClose}
          onSuccess={(msg) => handleDialogSuccess(msg)}
          onError={(msg) => handleDialogError(msg)}
          onSubmitStart={handleDialogSubmitStart}
          onSubmitEnd={handleDialogSubmitEnd}
        />
      </FormDialog>

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
      />
    </div>
  );
}

export default function ServiceTypePage() {
  return (
    <>
      <ServiceTypePageContent />
    </>
  );
}
